import { supabase } from './supabase.js'
import { DEFAULT_BINDINGS, normalizePlatform, normalizeSequence } from '../commands/commandModel.js'
import {
  COMMAND_MACRO_SCHEMA_VERSION,
  hasRegisteredId,
  normalizeCommandMacro,
} from './commandMacros.js'

export const COMMAND_CUSTOMIZATION_SCHEMA_VERSION = 1
export const USER_COMMAND_BINDINGS_TABLE = 'user_command_bindings'
export const USER_COMMAND_MACROS_TABLE = 'user_command_macros'

const BINDING_KEYS = new Set([
  'id',
  'commandId',
  'sequence',
  'platform',
  'schemaVersion',
  'enabled',
  'replacesDefaultBindingId',
])
const STROKE_KEYS = new Set(['key', 'modifiers'])
const STORED_ID_RE = /^[a-z0-9][a-z0-9._:-]{0,95}$/
const DEFAULT_BINDING_IDS = new Set(DEFAULT_BINDINGS.map(binding => binding.id))

function isPlainObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

function hasOnlyKeys(value, allowedKeys) {
  return Object.keys(value).every(key => allowedKeys.has(key))
}

function normalizeStoredSequence(value) {
  if (!Array.isArray(value) || value.length < 1 || value.length > 4) return null
  if (value.some(stroke => !isPlainObject(stroke) || !hasOnlyKeys(stroke, STROKE_KEYS))) return null
  try {
    return normalizeSequence(value, { maxLength: 4 })
  } catch {
    return null
  }
}

export async function resolveCommandPreferenceUserId(userId, supabaseClient = supabase) {
  if (userId) return userId
  try {
    const { data, error } = await supabaseClient.auth.getUser()
    if (error) return null
    return data?.user?.id || null
  } catch {
    return null
  }
}

export function normalizeCommandBindingPreference(value, {
  commandRegistry,
  registeredDefaultBindingIds = DEFAULT_BINDING_IDS,
} = {}) {
  if (!isPlainObject(value) || !hasOnlyKeys(value, BINDING_KEYS)) return null
  if (typeof value.id !== 'string' || !STORED_ID_RE.test(value.id)) return null
  if (typeof value.commandId !== 'string' || !STORED_ID_RE.test(value.commandId) || !hasRegisteredId(commandRegistry, value.commandId)) return null
  if (value.schemaVersion !== COMMAND_CUSTOMIZATION_SCHEMA_VERSION) return null
  if (typeof value.enabled !== 'boolean') return null
  if (value.platform != null && typeof value.platform !== 'string') return null
  const replacesDefaultBindingId = value.replacesDefaultBindingId ?? null
  if (replacesDefaultBindingId !== null && (
    typeof replacesDefaultBindingId !== 'string' ||
    !STORED_ID_RE.test(replacesDefaultBindingId) ||
    !hasRegisteredId(registeredDefaultBindingIds, replacesDefaultBindingId)
  )) return null
  const sequence = normalizeStoredSequence(value.sequence)
  if (!sequence) return null
  let platform
  try {
    platform = normalizePlatform(value.platform || 'all')
  } catch {
    return null
  }
  return {
    id: value.id,
    commandId: value.commandId,
    sequence,
    platform,
    replacesDefaultBindingId,
    schemaVersion: COMMAND_CUSTOMIZATION_SCHEMA_VERSION,
    enabled: value.enabled,
  }
}

function bindingFromRow(row) {
  return {
    id: row?.binding_id,
    commandId: row?.command_id,
    sequence: row?.sequence,
    platform: row?.platform,
    replacesDefaultBindingId: row?.replaces_default_binding_id ?? null,
    schemaVersion: row?.schema_version,
    enabled: row?.enabled,
  }
}

function macroFromRow(row) {
  return {
    id: row?.macro_id,
    name: row?.name,
    schemaVersion: row?.schema_version,
    enabled: row?.enabled,
    steps: row?.steps,
  }
}

export async function readCommandBindingPreferences({
  userId = null,
  commandRegistry,
  registeredDefaultBindingIds = DEFAULT_BINDING_IDS,
  supabaseClient = supabase,
} = {}) {
  const result = await readCommandBindingPreferencesResult({
    userId,
    commandRegistry,
    registeredDefaultBindingIds,
    supabaseClient,
  })
  return result.ok ? result.preferences : []
}

export async function readCommandBindingPreferencesResult({
  userId = null,
  commandRegistry,
  registeredDefaultBindingIds = DEFAULT_BINDING_IDS,
  supabaseClient = supabase,
} = {}) {
  const ownerId = await resolveCommandPreferenceUserId(userId, supabaseClient)
  if (!ownerId) return { ok: false, preferences: [] }
  try {
    const { data, error } = await supabaseClient
      .from(USER_COMMAND_BINDINGS_TABLE)
      .select('binding_id, command_id, sequence, platform, replaces_default_binding_id, schema_version, enabled')
      .eq('user_id', ownerId)
      .order('binding_id', { ascending: true })
    if (error || !Array.isArray(data)) return { ok: false, preferences: [] }
    const preferences = data
      .map(bindingFromRow)
      .map(value => normalizeCommandBindingPreference(value, { commandRegistry, registeredDefaultBindingIds }))
      .filter(Boolean)
    return { ok: true, preferences }
  } catch {
    return { ok: false, preferences: [] }
  }
}

export async function writeCommandBindingPreference({
  userId = null,
  preference,
  commandRegistry,
  registeredDefaultBindingIds = DEFAULT_BINDING_IDS,
  supabaseClient = supabase,
} = {}) {
  const ownerId = await resolveCommandPreferenceUserId(userId, supabaseClient)
  const normalized = normalizeCommandBindingPreference(preference, { commandRegistry, registeredDefaultBindingIds })
  if (!ownerId || !normalized) return false
  try {
    const { error } = await supabaseClient
      .from(USER_COMMAND_BINDINGS_TABLE)
      .upsert({
        user_id: ownerId,
        binding_id: normalized.id,
        command_id: normalized.commandId,
        sequence: normalized.sequence,
        platform: normalized.platform,
        replaces_default_binding_id: normalized.replacesDefaultBindingId,
        schema_version: normalized.schemaVersion,
        enabled: normalized.enabled,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'user_id,binding_id' })
    return !error
  } catch {
    return false
  }
}

export function prepareCommandBindingOverlay(preferences) {
  const userBindings = []
  const disabledDefaultBindingIds = new Set()
  for (const preference of Array.isArray(preferences) ? preferences : []) {
    if (!preference || typeof preference !== 'object') continue
    const {
      replacesDefaultBindingId,
      schemaVersion: _schemaVersion,
      ...binding
    } = preference
    userBindings.push(binding)
    if (replacesDefaultBindingId) disabledDefaultBindingIds.add(replacesDefaultBindingId)
  }
  return {
    userBindings,
    disabledDefaultBindingIds: [...disabledDefaultBindingIds].sort(),
  }
}

export async function deleteCommandBindingPreference({
  userId = null,
  bindingId,
  supabaseClient = supabase,
} = {}) {
  const ownerId = await resolveCommandPreferenceUserId(userId, supabaseClient)
  if (!ownerId || typeof bindingId !== 'string' || !STORED_ID_RE.test(bindingId)) return false
  try {
    const { error } = await supabaseClient
      .from(USER_COMMAND_BINDINGS_TABLE)
      .delete()
      .eq('user_id', ownerId)
      .eq('binding_id', bindingId)
    return !error
  } catch {
    return false
  }
}

export async function readCommandMacroPreferences({
  userId = null,
  commandRegistry,
  registeredPromptIds = new Set(),
  supabaseClient = supabase,
} = {}) {
  const result = await readCommandMacroPreferencesResult({
    userId,
    commandRegistry,
    registeredPromptIds,
    supabaseClient,
  })
  return result.ok ? result.preferences : []
}

export async function readCommandMacroPreferencesResult({
  userId = null,
  commandRegistry,
  registeredPromptIds = new Set(),
  supabaseClient = supabase,
} = {}) {
  const ownerId = await resolveCommandPreferenceUserId(userId, supabaseClient)
  if (!ownerId) return { ok: false, preferences: [] }
  try {
    const { data, error } = await supabaseClient
      .from(USER_COMMAND_MACROS_TABLE)
      .select('macro_id, name, schema_version, enabled, steps')
      .eq('user_id', ownerId)
      .order('macro_id', { ascending: true })
    if (error || !Array.isArray(data)) return { ok: false, preferences: [] }
    const preferences = data
      .map(macroFromRow)
      .map(value => normalizeCommandMacro(value, { commandRegistry, registeredPromptIds }))
      .filter(Boolean)
    return { ok: true, preferences }
  } catch {
    return { ok: false, preferences: [] }
  }
}

export async function writeCommandMacroPreference({
  userId = null,
  macro,
  commandRegistry,
  registeredPromptIds = new Set(),
  supabaseClient = supabase,
} = {}) {
  const ownerId = await resolveCommandPreferenceUserId(userId, supabaseClient)
  const normalized = normalizeCommandMacro(macro, { commandRegistry, registeredPromptIds })
  if (!ownerId || !normalized || normalized.schemaVersion !== COMMAND_MACRO_SCHEMA_VERSION) return false
  try {
    const { error } = await supabaseClient
      .from(USER_COMMAND_MACROS_TABLE)
      .upsert({
        user_id: ownerId,
        macro_id: normalized.id,
        name: normalized.name,
        schema_version: normalized.schemaVersion,
        enabled: normalized.enabled,
        steps: normalized.steps,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'user_id,macro_id' })
    return !error
  } catch {
    return false
  }
}

export async function deleteCommandMacroPreference({
  userId = null,
  macroId,
  supabaseClient = supabase,
} = {}) {
  const ownerId = await resolveCommandPreferenceUserId(userId, supabaseClient)
  if (!ownerId || typeof macroId !== 'string' || !STORED_ID_RE.test(macroId)) return false
  try {
    const { error } = await supabaseClient
      .from(USER_COMMAND_MACROS_TABLE)
      .delete()
      .eq('user_id', ownerId)
      .eq('macro_id', macroId)
    return !error
  } catch {
    return false
  }
}
